import React, { useRef, useState, useEffect } from "react";
import axios from "axios";
import host from "../../global";
import { toast } from "react-toastify";
import { useMediaPipe } from "../../contexts/MediaPipeContext.jsx";
import useMultiMonitor from "./useMultiMonitor.jsx";
import { MODEL_URLS, COOLDOWN_PERIOD } from "./Interviews/constants.js";
// Default/min/max footprint for the floating, draggable camera preview.
const DEFAULT_WIDTH = 280;
const DEFAULT_HEIGHT = 180;
const MIN_WIDTH = 160;
const MIN_HEIGHT = 100;
const MAX_WIDTH = 560;
const MAX_HEIGHT = 380;

// Lean proctoring component for the coding platform: fullscreen exit,
// tab switching, multiple monitors, prohibited-object detection, and
// multiple-face detection only — no hand/gaze/posture tracking and no
// copy-paste/devtools blocking (see ProctoringSystem.jsx for the full set).
const CodingProctoringSystem = ({
  isEnabled = false,
  interviewId,
  isTestActive = false,
  onMultipleFaceViolation = () => {},
  onProhibitedObjectViolation = () => {},
  onStickyAlert = () => {},
  onFullscreenExitViolation = () => {},
  onTabSwitchViolation = () => {},
  onFullscreenChange = () => {},
  className = "",
}) => {
  const { getDetectors, isInitialized: isMediaPipeReady, configs } = useMediaPipe();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const lastDetectionTime = useRef(0);
  const lastLoggedMap = useRef({});
  const lastMultiFaceViolationTime = useRef(null);
  const lastProhibitedObjectViolationTime = useRef(null);

  const [faceDetector, setFaceDetector] = useState(null);
  const [objectDetector, setObjectDetector] = useState(null);
  const [isCameraActive, setIsCameraActive] = useState(false);

  // Floating preview — draggable position + resizable size
  const [position, setPosition] = useState(() => ({
    x: Math.max(0, window.innerWidth - DEFAULT_WIDTH - 24),
    y: 64,
  }));
  const [size, setSize] = useState({ width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT });

  const isMultiMonitor = useMultiMonitor();
  const faceTracker = useRef({ primaryFace: null, faceHistory: [], maxHistorySize: 10 });

  // SECTION: Face validation helpers (lifted from ProctoringSystem.jsx)

  const calculateFaceArea = (boundingBox) => {
    if (!boundingBox) return 0;
    return boundingBox.width * boundingBox.height;
  };

  const calculateFaceCenter = (boundingBox) => {
    if (!boundingBox) return { x: 0, y: 0 };
    return {
      x: boundingBox.originX + boundingBox.width / 2,
      y: boundingBox.originY + boundingBox.height / 2,
    };
  };

  const isValidFaceDetection = (detection) => {
    if (!detection.boundingBox) return false;
    const area = calculateFaceArea(detection.boundingBox);
    const confidence = detection.categories[0]?.score || 0;
    const width = detection.boundingBox.width;
    const height = detection.boundingBox.height;
    const videoArea = (videoRef.current?.videoWidth || 640) * (videoRef.current?.videoHeight || 480);
    const relativeArea = area / videoArea;
    const aspectRatio = width / height;
    const isReasonableAspectRatio = aspectRatio > 0.5 && aspectRatio < 2.0;

    let requiredConfidence;
    if (relativeArea > 0.05) requiredConfidence = 0.6;
    else if (relativeArea > 0.01) requiredConfidence = 0.7;
    else requiredConfidence = 0.8;

    return (
      relativeArea > 0.002 &&
      relativeArea < 0.5 &&
      confidence > requiredConfidence &&
      width > 20 &&
      height > 25 &&
      isReasonableAspectRatio &&
      !(width < 30 && height > width * 2) &&
      !(height < 30 && width > height * 2)
    );
  };

  const updateFaceTracker = (validFaces) => {
    const currentTime = performance.now();
    if (validFaces.length === 1) {
      const face = validFaces[0];
      const faceInfo = {
        center: calculateFaceCenter(face.boundingBox),
        area: calculateFaceArea(face.boundingBox),
        confidence: face.categories[0]?.score || 0,
        timestamp: currentTime,
        boundingBox: face.boundingBox,
      };
      faceTracker.current.faceHistory.push(faceInfo);
      if (faceTracker.current.faceHistory.length > faceTracker.current.maxHistorySize) {
        faceTracker.current.faceHistory.shift();
      }
      faceTracker.current.primaryFace = faceInfo;
    }
  };

  const validateMultipleFaces = (faces) => {
    if (faces.length <= 1) return faces;
    const validatedFaces = [];
    const primaryFace = faceTracker.current.primaryFace;
    const sortedFaces = faces.sort(
      (a, b) => calculateFaceArea(b.boundingBox) - calculateFaceArea(a.boundingBox),
    );

    if (primaryFace && faceTracker.current.faceHistory.length >= 3) {
      for (const face of sortedFaces) {
        const faceCenter = calculateFaceCenter(face.boundingBox);
        const faceArea = calculateFaceArea(face.boundingBox);
        const confidence = face.categories[0]?.score || 0;
        const distance = Math.sqrt(
          Math.pow(faceCenter.x - primaryFace.center.x, 2) + Math.pow(faceCenter.y - primaryFace.center.y, 2),
        );
        const areaRatio = Math.min(faceArea, primaryFace.area) / Math.max(faceArea, primaryFace.area);

        const isLikelyPrimaryFace = distance < 80 && areaRatio > 0.5 && confidence > 0.65;
        const isLikelySecondaryFace =
          distance > 100 &&
          confidence > 0.75 &&
          ((faceArea > primaryFace.area * 0.15 && faceArea < primaryFace.area * 1.5) ||
            (faceArea > 400 && confidence > 0.85));

        if (isLikelyPrimaryFace || isLikelySecondaryFace) validatedFaces.push(face);
      }
    } else {
      for (const face of sortedFaces) {
        const confidence = face.categories[0]?.score || 0;
        const faceArea = calculateFaceArea(face.boundingBox);
        if (confidence > 0.75 || (confidence > 0.65 && faceArea > 1000)) validatedFaces.push(face);
      }
    }
    return validatedFaces;
  };

  const detectProhibitedObjects = (objectResults) => {
    const prohibitedItems = ["cell phone", "laptop", "smartwatch", "tv"];
    const detected = [];
    objectResults.detections.forEach((detection) => {
      detection.categories.forEach((category) => {
        const categoryName = category.categoryName.toLowerCase();
        const confidence = category.score;
        const isProhibited = prohibitedItems.some(
          (item) => categoryName.includes(item) || item.includes(categoryName),
        );
        if (isProhibited && confidence > configs.SCORE_THRESHOLD) {
          detected.push({ name: categoryName, confidence });
        }
      });
    });
    return detected;
  };

  // SECTION: Screenshot + violation logging (same endpoint as ProctoringSystem.jsx)

  const captureScreenshot = () => {
    if (!videoRef.current) return null;
    const video = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.8);
    });
  };

  const logViolationToBackend = async (violation_type, meta = {}) => {
    try {
      const formData = new FormData();
      formData.append("violation_type", violation_type);
      formData.append("timestamp", new Date().toISOString());
      if (meta.device_name) formData.append("device_name", meta.device_name);
      if (meta.confidence_percent) formData.append("confidence_percent", meta.confidence_percent.toString());
      if (meta.img) formData.append("screenshot", meta.img, "screenshot.jpg");
      await axios.post(`${host}/log_coding_violation/${interviewId}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(sessionStorage.getItem(`candidateToken_${interviewId}`)
            ? { Authorization: `Bearer ${sessionStorage.getItem(`candidateToken_${interviewId}`)}` }
            : {}),
        },
      });
    } catch (error) {
      console.error(`Failed to log violation: ${violation_type}`, error);
    }
  };

  const logViolationWithCooldown = async (violation_type, meta = {}) => {
    try {
      let cooldownKey = violation_type;
      if (violation_type === "prohibited_item" && meta.device_name) {
        cooldownKey = `prohibited_item_${meta.device_name}`;
      }
      const now = Date.now();
      const lastLoggedTime = lastLoggedMap.current[cooldownKey];
      if (!lastLoggedTime || now - lastLoggedTime >= configs.VIOLATION_COOLDOWN_MS) {
        lastLoggedMap.current[cooldownKey] = now;
        const screenshotBlob = await captureScreenshot();
        if (!screenshotBlob) return logViolationToBackend(violation_type, meta);

        const formData = new FormData();
        formData.append("screenshot", screenshotBlob, "screenshot.jpg");
        formData.append("violation_type", violation_type);
        if (meta.device_name) formData.append("device_name", meta.device_name);
        if (meta.confidence_percent) formData.append("confidence_percent", meta.confidence_percent.toString());

        await axios.post(`${host}/log_coding_violation/${interviewId}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            ...(sessionStorage.getItem(`candidateToken_${interviewId}`)
              ? { Authorization: `Bearer ${sessionStorage.getItem(`candidateToken_${interviewId}`)}` }
              : {}),
          },
        });
      }
    } catch (error) {
      console.error("Failed to log violation with screenshot:", error);
    }
  };

  // SECTION: Camera lifecycle (lifted from ProctoringSystem.jsx, trimmed)

  const startCamera = async (retryCount = 0) => {
    try {
      if (streamRef.current && streamRef.current.active) {
        if (videoRef.current && videoRef.current.srcObject !== streamRef.current) {
          videoRef.current.srcObject = streamRef.current;
          try {
            await videoRef.current.play();
          } catch {
            await new Promise((resolve) => setTimeout(resolve, 100));
            await videoRef.current.play();
          }
        }
        setIsCameraActive(true);
        return;
      }

      if (navigator.permissions) {
        try {
          const cameraPermission = await navigator.permissions.query({ name: "camera" });
          if (cameraPermission.state === "denied") {
            toast.error("Camera access denied. Please enable permissions in browser settings.");
            setIsCameraActive(false);
            return;
          }
        } catch {}
      }

      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      streamRef.current = stream;
      if (videoRef.current) {
        if (videoRef.current.srcObject) videoRef.current.srcObject = null;
        videoRef.current.srcObject = stream;
        try {
          await videoRef.current.play();
        } catch (playError) {
          const delay = Math.min(100 * Math.pow(2, retryCount), 1000);
          await new Promise((resolve) => setTimeout(resolve, delay));
          await videoRef.current.play();
        }
        await new Promise((resolve) => {
          if (videoRef.current.readyState >= 2) return resolve();
          videoRef.current.onloadedmetadata = resolve;
        });
        if (canvasRef.current) {
          canvasRef.current.width = videoRef.current.videoWidth || 640;
          canvasRef.current.height = videoRef.current.videoHeight || 480;
        }
      }
      setIsCameraActive(true);
    } catch (error) {
      if (error.name === "AbortError" && retryCount < 2) {
        setTimeout(() => startCamera(retryCount + 1), 500 + retryCount * 500);
        return;
      }
      if (error.name === "NotAllowedError") {
        toast.error("Camera access denied. Please enable camera permissions and refresh the page.");
      } else if (error.name === "NotFoundError") {
        toast.error("No camera found. Please connect a camera and try again.");
      } else if (error.name === "NotReadableError") {
        toast.error("Camera is being used by another application. Please close other apps and try again.");
      } else {
        toast.error("Couldn't access camera. Please check permissions and try again.");
      }
      setIsCameraActive(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setIsCameraActive(false);
  };

  // SECTION: MediaPipe init — only faceDetector + objectDetector

  const initializeMediaPipe = async () => {
    if (!isEnabled || !configs) return;

    if (isMediaPipeReady) {
      const detectors = getDetectors();
      if (detectors.faceDetector && detectors.objectDetector) {
        setFaceDetector(detectors.faceDetector);
        setObjectDetector(detectors.objectDetector);
        return;
      }
    }

    try {
      const { FaceDetector, ObjectDetector, FilesetResolver } = await import("@mediapipe/tasks-vision");
      const vision = await FilesetResolver.forVisionTasks(MODEL_URLS.WASM_BASE);
      const [fd, od] = await Promise.all([
        FaceDetector.createFromOptions(vision, {
          baseOptions: { modelAssetPath: MODEL_URLS.FACE_DETECTOR, delegate: "GPU" },
          runningMode: "VIDEO",
          minDetectionConfidence: configs.MIN_FACE_DETECTION_CONFIDENCE,
        }),
        ObjectDetector.createFromOptions(vision, {
          baseOptions: { modelAssetPath: MODEL_URLS.OBJECT_DETECTOR, delegate: "GPU" },
          scoreThreshold: configs.SCORE_THRESHOLD,
          runningMode: "VIDEO",
        }),
      ]);
      setFaceDetector(fd);
      setObjectDetector(od);
    } catch (error) {
      console.error("Error initializing MediaPipe:", error);
      toast.error("Failed to initialize proctoring system.");
    }
  };

  // SECTION: Main detection loop — multi-face + object detection only

  const runProctoring = async () => {
    if (!isEnabled || !isTestActive || !configs || !faceDetector || !objectDetector || !videoRef.current || !canvasRef.current) {
      return;
    }
    const video = videoRef.current;
    if (!video.videoWidth || !video.videoHeight) return;
    const now = performance.now();
    if (lastDetectionTime.current !== 0 && now - lastDetectionTime.current < 200) return;
    lastDetectionTime.current = now;

    const faceResults = await faceDetector.detectForVideo(video, now);
    const confidentDetections = faceResults.detections.filter(
      (detection) => detection.categories[0]?.score >= configs.MIN_FACE_DETECTION_CONFIDENCE,
    );
    const validFaceDetections = confidentDetections.filter(isValidFaceDetection);
    const finalValidatedFaces = validateMultipleFaces(validFaceDetections);
    updateFaceTracker(finalValidatedFaces);

    if (finalValidatedFaces.length > 1) {
      const lastTime = lastMultiFaceViolationTime.current;
      if (!lastTime || now - lastTime >= COOLDOWN_PERIOD.MULTIPLE_FACE_COOLDOWN_MS) {
        lastMultiFaceViolationTime.current = now;
        onMultipleFaceViolation();
        logViolationWithCooldown("multiple_faces", { face_count: finalValidatedFaces.length });
      }
    }

    const objectResults = await objectDetector.detectForVideo(video, now);
    const prohibitedObjects = detectProhibitedObjects(objectResults);
    prohibitedObjects.forEach((obj) => {
      const lastTime = lastProhibitedObjectViolationTime.current;
      if (!lastTime || now - lastTime >= COOLDOWN_PERIOD.PROHIBITED_OBJECT_COOLDOWN_MS) {
        lastProhibitedObjectViolationTime.current = now;
        onProhibitedObjectViolation();
        logViolationWithCooldown("prohibited_item", {
          device_name: obj.name,
          confidence_percent: Math.round(obj.confidence * 100),
        });
      }
    });
  };

  // SECTION: Effects

  useEffect(() => {
    const handleFullscreenChange = async () => {
      const fullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      onFullscreenChange(fullscreen);
      if (!fullscreen && isTestActive) {
        onFullscreenExitViolation();
        const screenshot = await captureScreenshot();
        logViolationToBackend("fullscreen_exit", { img: screenshot });
      }
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
      document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
    };
  }, [isTestActive]);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.hidden && isTestActive) {
        const screenshot = await captureScreenshot();
        onTabSwitchViolation();
        logViolationToBackend("tab_switch", { img: screenshot });
      }
    };
    if (isTestActive) document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [isTestActive]);

  useEffect(() => {
    if (isMultiMonitor && isTestActive) {
      onStickyAlert("multi_monitor");
      logViolationToBackend("multi_monitor_detected");
    }
  }, [isMultiMonitor, isTestActive]);

  useEffect(() => {
    if (isEnabled) initializeMediaPipe();
  }, [isEnabled, isMediaPipeReady, configs]);

  useEffect(() => {
    if (isEnabled && !isCameraActive && !streamRef.current?.active) startCamera();
  }, [isEnabled]);

  useEffect(() => {
    let interval;
    if (isEnabled && isTestActive && isCameraActive && faceDetector && objectDetector) {
      interval = setInterval(runProctoring, 200);
    }
    return () => clearInterval(interval);
  }, [isEnabled, isTestActive, isCameraActive, faceDetector, objectDetector]);

  useEffect(() => {
    return () => stopCamera();
  }, []);

  // Keep the floating box on-screen if the window is resized
  useEffect(() => {
    const handleWindowResize = () => {
      setPosition((prev) => ({
        x: Math.min(prev.x, Math.max(0, window.innerWidth - size.width)),
        y: Math.min(prev.y, Math.max(0, window.innerHeight - size.height)),
      }));
    };
    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, [size.width, size.height]);

  // SECTION: Drag-to-move + drag-to-resize (same closure-based pattern used
  // elsewhere in CodingProblemsPage.jsx for its panel dividers)

  const handleDragMouseDown = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const originX = position.x;
    const originY = position.y;

    const onMove = (ev) => {
      setPosition({
        x: Math.min(Math.max(0, originX + (ev.clientX - startX)), window.innerWidth - size.width),
        y: Math.min(Math.max(0, originY + (ev.clientY - startY)), window.innerHeight - size.height),
      });
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = size.width;
    const startHeight = size.height;

    const onMove = (ev) => {
      setSize({
        width: Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth + (ev.clientX - startX))),
        height: Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, startHeight + (ev.clientY - startY))),
      });
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  return (
    <>
      <div
        className={`fixed rounded-xl overflow-hidden shadow-2xl border-2 border-white/20 bg-black select-none ${className}`}
        style={{ left: position.x, top: position.y, width: size.width, height: size.height, zIndex: 40, cursor: "move" }}
        onMouseDown={handleDragMouseDown}
      >
        <video ref={videoRef} autoPlay muted className="w-full h-full object-cover pointer-events-none" />
        <canvas ref={canvasRef} className="hidden" />
        <div
          onMouseDown={handleResizeMouseDown}
          title="Drag to resize"
          className="absolute bottom-0 right-0 w-5 h-5 cursor-nwse-resize"
          style={{ background: "linear-gradient(135deg, transparent 50%, rgba(255,255,255,0.6) 50%)" }}
        />
      </div>

      {/* Usage hint — follows the preview's position/size so it stays anchored just below it */}
      <div
        className="fixed text-[10px] leading-snug text-white/80 bg-black/55 backdrop-blur-sm rounded-md px-2 py-1 text-center pointer-events-none select-none"
        style={{ left: position.x, top: position.y + size.height + 6, width: size.width, zIndex: 40 }}
      >
        Click &amp; hold the bottom-right corner to resize · Click &amp; hold the window to move it
      </div>
    </>
  );
};

export default CodingProctoringSystem;
